import { z } from 'zod';
/**
 * Integrations (owner decision 12.10.2026, docs/dev/api-integrations.md): connections of an organization to an
 * external system — today TeamProQ (Real Estate Pilot AG) — their property selection, the import runs (dry run and
 * apply) and the run report. Phase 1 is import only: DAMOKLES reads from TeamProQ and never writes back; the mode
 * `bidirectional` exists in the data model but is only settable through the server CLI. Routes under
 * `/api/settings/organization/integrations` (permission `settings.organization = yes`). German labels are own
 * strings (no design frame exists for this settings page; D-189).
 */
// ---------------------------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------------------------
export const INTEGRATION_CONNECTION_MODES = ['import_only', 'bidirectional'];
export const integrationConnectionModeLabels = {
    import_only: 'Nur Import (TeamProQ führend)',
    bidirectional: 'Synchronisation in beide Richtungen',
};
export const IMPORT_RUN_MODES = ['dry_run', 'apply'];
export const importRunModeLabels = {
    dry_run: 'Probelauf',
    apply: 'Import',
};
export const IMPORT_RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed'];
export const importRunStatusLabels = {
    queued: 'Wartet',
    running: 'Läuft',
    succeeded: 'Abgeschlossen',
    failed: 'Fehlgeschlagen',
};
export const PROPERTY_SELECTION_MODES = ['include', 'exclude'];
export const propertySelectionModeLabels = {
    include: 'Nur ausgewählte Objekte importieren',
    exclude: 'Alle Objekte außer den ausgewählten importieren',
};
/** Entities the report counts (the `external_ref` entities plus the unit links, which have no own reference). */
export const IMPORT_REPORT_ENTITIES = [
    'property',
    'unit',
    'unit_link',
    'unit_cost_item',
    'external_reservation',
    'tenancy',
    'document',
    'media_item',
];
export const importReportEntityLabels = {
    property: 'Objekte',
    unit: 'Einheiten',
    unit_link: 'Verknüpfte Einheiten',
    unit_cost_item: 'Kostenpositionen',
    external_reservation: 'Reservierungen (TeamProQ)',
    tenancy: 'Mietverhältnisse',
    document: 'Dokumente',
    media_item: 'Bilder',
};
export const IMPORT_OUTCOMES = [
    'new',
    'changed',
    'unchanged',
    'gone',
    'conflict',
    'failed',
    'skipped',
];
export const importOutcomeLabels = {
    new: 'Neu',
    changed: 'Geändert',
    unchanged: 'Unverändert',
    gone: 'Nicht mehr geliefert',
    conflict: 'Konflikt',
    failed: 'Fehler',
    skipped: 'Übersprungen',
};
/** Reasons of a conflict row. */
export const IMPORT_CONFLICT_REASONS = [
    'external_block_vs_live_reservation',
    'unit_number_taken',
    'tenancy_without_unit',
];
export const importConflictReasonLabels = {
    external_block_vs_live_reservation: 'TeamProQ meldet die Einheit als reserviert oder verkauft, in DAMOKLES läuft eine Reservierung.',
    unit_number_taken: 'Im Objekt gibt es bereits eine Einheit mit dieser Bezeichnung, die nicht aus TeamProQ stammt.',
    tenancy_without_unit: 'Die Einheit des Mietvertrags ist nicht importiert.',
};
/** Reasons of a hint row (information without a decision). */
export const IMPORT_HINT_REASONS = [
    'property_status_kept',
    'global_unit_skipped',
    'sale_contract_skipped',
    'contract_in_preparation_skipped',
    'tenant_contact_unavailable',
    'synopsis_empty',
    'file_unclassified',
    'unknown_value',
    'land_transfer_tax_differs',
];
export const importHintReasonLabels = {
    property_status_kept: 'Der Vertriebsstatus des Objekts wird nur beim ersten Import gesetzt; DAMOKLES behält seinen Status.',
    global_unit_skipped: 'Gesamtobjekt-Eintrag aus TeamProQ, keine verkaufbare Einheit — nicht importiert.',
    sale_contract_skipped: 'Kaufvertrag aus TeamProQ — wird nicht als Mietverhältnis angelegt; die Einheit ist als extern verkauft markiert.',
    contract_in_preparation_skipped: 'Mietvertrag in Vorbereitung — wird erst als Mietverhältnis angelegt, wenn er läuft.',
    tenant_contact_unavailable: 'Der Kontakt des Mieters ist in TeamProQ nicht freigegeben — die Mieterangaben bleiben leer bzw. unverändert.',
    synopsis_empty: 'Die Exposé-Texte der Einheit sind in TeamProQ leer.',
    file_unclassified: 'Dokumenttyp nicht erkannt — bitte manuell zuordnen.',
    unknown_value: 'Unbekannter Wert aus TeamProQ — Feld bleibt leer.',
    land_transfer_tax_differs: 'TeamProQ-Grunderwerbsteuer weicht vom Landessatz ab — DAMOKLES rechnet mit dem Satz des Bundeslands.',
};
// ---------------------------------------------------------------------------------------------
// Run report (written by the importer into `import_run.report`, rendered by Einstellungen › Integrationen)
// ---------------------------------------------------------------------------------------------
export const importOutcomeCountsSchema = z.object({
    new: z.number().int(),
    changed: z.number().int(),
    unchanged: z.number().int(),
    gone: z.number().int(),
    conflict: z.number().int(),
    failed: z.number().int(),
    skipped: z.number().int(),
});
export const importReportCountsSchema = z.object({
    property: importOutcomeCountsSchema,
    unit: importOutcomeCountsSchema,
    unit_link: importOutcomeCountsSchema,
    unit_cost_item: importOutcomeCountsSchema,
    external_reservation: importOutcomeCountsSchema,
    tenancy: importOutcomeCountsSchema,
    document: importOutcomeCountsSchema,
    media_item: importOutcomeCountsSchema,
});
export const importConflictSchema = z.object({
    entity: z.enum(IMPORT_REPORT_ENTITIES),
    externalId: z.string(),
    entityId: z.string().nullable(),
    field: z.string().nullable(),
    reason: z.enum(IMPORT_CONFLICT_REASONS),
    reasonLabel: z.string(),
    /** Display text of the DAMOKLES side (e.g. „Reservierung WE 3 · wartet auf Unterschrift\"). */
    damoklesValue: z.string().nullable(),
    externalValue: z.string().nullable(),
});
export const importErrorSchema = z.object({
    entity: z.enum(IMPORT_REPORT_ENTITIES),
    externalId: z.string(),
    message: z.string(),
});
export const importHintSchema = z.object({
    entity: z.enum(IMPORT_REPORT_ENTITIES),
    externalId: z.string(),
    entityId: z.string().nullable(),
    reason: z.enum(IMPORT_HINT_REASONS),
    reasonLabel: z.string(),
    detail: z.string().nullable(),
});
export const importReportPropertySchema = z.object({
    externalId: z.string(),
    name: z.string(),
    /** TeamProQ folder („category\"). */
    category: z.string().nullable(),
    included: z.boolean(),
    damoklesPropertyId: z.string().nullable(),
    /** Units TeamProQ delivered for the object in this run (global entries excluded). */
    unitCount: z.number().int(),
});
export const importRunReportSchema = z.object({
    counts: importReportCountsSchema,
    conflicts: z.array(importConflictSchema),
    errors: z.array(importErrorSchema),
    hints: z.array(importHintSchema),
    properties: z.array(importReportPropertySchema),
    files: z.object({
        /** Files fetched from TeamProQ in this run (apply: stored; dry run: only counted as to be fetched). */
        downloaded: z.number().int(),
        /** Files whose bytes DAMOKLES already had (same sha256 for the same owner) — not stored again. */
        reused: z.number().int(),
        bytes: z.number().int(),
    }),
    /** Progress while the run is running (the UI polls the run). */
    progress: z.object({ processed: z.number().int(), total: z.number().int(), phase: z.string() }),
    durationMs: z.number().int(),
});
export function emptyOutcomeCounts() {
    return { new: 0, changed: 0, unchanged: 0, gone: 0, conflict: 0, failed: 0, skipped: 0 };
}
export function emptyImportRunReport() {
    const counts = Object.fromEntries(IMPORT_REPORT_ENTITIES.map((entity) => [entity, emptyOutcomeCounts()]));
    return {
        counts,
        conflicts: [],
        errors: [],
        hints: [],
        properties: [],
        files: { downloaded: 0, reused: 0, bytes: 0 },
        progress: { processed: 0, total: 0, phase: 'queued' },
        durationMs: 0,
    };
}
/** Sum of the outcome counts over all entities (the run list's summary line). */
export function totalOutcomeCounts(counts) {
    const total = emptyOutcomeCounts();
    for (const entity of IMPORT_REPORT_ENTITIES) {
        for (const outcome of IMPORT_OUTCOMES)
            total[outcome] += counts[entity][outcome];
    }
    return total;
}
// ---------------------------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------------------------
export const propertySelectionSchema = z.object({
    mode: z.enum(PROPERTY_SELECTION_MODES),
    /** TeamProQ object ids (digits). */
    externalIds: z.array(z.string().regex(/^[0-9A-Za-z_-]{1,64}$/)).max(500),
});
const personSchema = z.object({ id: z.string(), name: z.string() });
export const importRunSummarySchema = z.object({
    id: z.string(),
    connectionId: z.string(),
    mode: z.enum(IMPORT_RUN_MODES),
    modeLabel: z.string(),
    status: z.enum(IMPORT_RUN_STATUSES),
    statusLabel: z.string(),
    /** null = started by the hourly schedule („automatisch\"). */
    startedBy: personSchema.nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
    /** Sum over all entities; null while the run has not written a report yet. */
    totals: importOutcomeCountsSchema.nullable(),
    progress: z.object({ processed: z.number().int(), total: z.number().int(), phase: z.string() }).nullable(),
});
export const importRunDetailSchema = importRunSummarySchema.extend({
    report: importRunReportSchema.nullable(),
});
export const integrationConnectionSchema = z.object({
    id: z.string(),
    source: z.literal('teamproq'),
    sourceLabel: z.string(),
    name: z.string(),
    connectionKey: z.string(),
    /** Host of the TeamProQ workspace („nico-kremer-immobilienvermittlung.teamproq.de\"); the token is never returned. */
    host: z.string(),
    mode: z.enum(INTEGRATION_CONNECTION_MODES),
    modeLabel: z.string(),
    /** Phase 1: always false — no code path writes to the external system. */
    writeEnabled: z.boolean(),
    active: z.boolean(),
    autoSync: z.boolean(),
    credentialsVerifiedAt: z.string().nullable(),
    lastRunAt: z.string().nullable(),
    propertySelection: propertySelectionSchema,
    lastRun: importRunSummarySchema.nullable(),
    /** The queued or running run, if any (a second start answers 409). */
    activeRunId: z.string().nullable(),
});
export const integrationConnectionsResponseSchema = z.object({ items: z.array(integrationConnectionSchema) });
export const externalPropertySchema = z.object({
    externalId: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    city: z.string().nullable(),
    addressLine: z.string().nullable(),
    /** From TeamProQ's `unit-ids?object-id=`; null when TeamProQ does not answer that list. */
    unitCount: z.number().int().nullable(),
    /** Per the current selection of the connection. */
    included: z.boolean(),
    damoklesPropertyId: z.string().nullable(),
});
export const externalPropertiesResponseSchema = z.object({
    items: z.array(externalPropertySchema),
    selection: propertySelectionSchema,
    /** When the list was read from TeamProQ (cached for 5 minutes). */
    fetchedAt: z.string(),
});
export const updateIntegrationConnectionBodySchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    active: z.boolean().optional(),
    autoSync: z.boolean().optional(),
})
    .strict();
export const startImportRunBodySchema = z.object({ mode: z.enum(IMPORT_RUN_MODES) }).strict();
export const importRunsResponseSchema = z.object({ items: z.array(importRunSummarySchema) });
//# sourceMappingURL=integrations.js.map